import React, {
  useEffect, useState,
} from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { navigate } from 'gatsby';
import {
  Button,
  Badge,
  Alert,
  DescriptionList,
  BadgeProps,
} from '@printdeal/ui-library';
import { useAddQuoteToCartMutation } from '@printdeal/checkout-frontend';
import QuoteLineItemList from './QuotesLineItemList';
import { messages } from '../../../translations/quote';
import useSystemPages from '../../../../../hooks/useSystemPages';
import ExtendQuoteModal from '../../modals/ExtendQuoteModal';
import {
  dispatchDataLayerEventOnExtendQuote,
  dispatchDataLayerEventOnAddQuoteLineItemsToCart,
} from '../../../../../helpers/dataLayerHelpers/quoteDataLayerHelper';
import { Quote, QuoteStatus } from '../../../../../types/quotes/quotes';
import { colorToSemanticVariant } from '../../../../../helpers/brand';

const STATUS = {
  EXPIRED: 'expired',
  WITHDRAWN: 'withdrawn',
};

interface Props {
  quote: Quote | null;
  statuses: QuoteStatus[] | null;
}

const QuoteDetail = ({
  quote,
  statuses,
}: Props) => {
  const {
    isExpired,
    quoteState,
    createdAt,
    quoteExpiryDate,
    quoteReference,
    quoteLineItems,
    quoteNumber,
    externalNote,
    quoteId,
  } = quote as Quote;

  const [cartPage] = useSystemPages('cart');
  const [currentStatus, setCurrentStatus] = useState<QuoteStatus | null>(null);
  const [selectedLineItems, setSelectedLineItems] = useState<string[]>([]);
  const [quoteCreated, setQuoteCreated] = useState(false);
  const [shouldDisableAddToCartButton, setShouldDisableAddToCartButton] = useState(true);

  const addQuoteToCartMutation = useAddQuoteToCartMutation();
  useEffect(() => {
    if (addQuoteToCartMutation.isSuccess) {
      dispatchDataLayerEventOnAddQuoteLineItemsToCart();
      navigate(cartPage.path);
    }
  }, [addQuoteToCartMutation.isSuccess]);

  // Extend quote modal interaction
  const [extendQuoteModalSettings, setExtendQuoteModalSettings] = useState<{
    open: boolean;
    selectedLineItemIds: string | string[]
  }>({ open: false, selectedLineItemIds: [] });
  const openExtendQuoteModal = () => {
    dispatchDataLayerEventOnExtendQuote();
    setExtendQuoteModalSettings({
      open: true,
      selectedLineItemIds: selectedLineItems,
    });
  };

  const isQuoteExpired = () =>
    (isExpired === true)
    || (quoteState === STATUS.WITHDRAWN);

  useEffect(() => {
    const quoteStatus = isExpired ? STATUS.EXPIRED : quoteState;
    const result = statuses?.find((state) => state.name === quoteStatus) || null;
    setCurrentStatus(result);
  }, []);

  useEffect(() => {
    setShouldDisableAddToCartButton(
      isQuoteExpired() || addQuoteToCartMutation.status === 'pending' || selectedLineItems.length === 0,
    );
  }, [addQuoteToCartMutation.status, selectedLineItems]);

  const addQuoteToCart = () => {
    addQuoteToCartMutation.mutate(
      selectedLineItems
        .filter((selectedLineItem) => selectedLineItem !== 'select-all')
        .map((lineItem) => ({
          quoteId: quoteNumber,
          quoteLineItemId: lineItem,
        })),
    );
  };

  return (
    <>
      {extendQuoteModalSettings.open && (
        <ExtendQuoteModal
          quoteId={quoteId}
          quoteLineItemId={extendQuoteModalSettings.selectedLineItemIds}
          closeModal={(quoteCreateStatus) => {
            setQuoteCreated(quoteCreateStatus);
            setExtendQuoteModalSettings({ open: false, selectedLineItemIds: [] });
          }}
          quoteLineItems={quoteLineItems}
        />
      )}
      {addQuoteToCartMutation.isError && (
      <Alert variant="error">
        <FormattedMessage
          id="account.quotes.form.error.submitting"
          defaultMessage="Something went wrong while submitting your quote."
        />
      </Alert>
      )}
      {quoteCreated && (
      <Alert variant="success">
        <FormattedMessage
          id="quotes.created.success"
          defaultMessage="Your offer has been requested! you will receive a response from us within 24 hours on info@drukwerkdeal.nl"
        />
      </Alert>
      )}
      <DescriptionList>
        {quoteState && currentStatus && (
        <>
          <DescriptionList.Title>
            <FormattedMessage
              id="quote.details.status"
              defaultMessage="Status"
            />
          </DescriptionList.Title>
          <DescriptionList.Content>
            <Badge variant={colorToSemanticVariant(currentStatus?.color || 'blue') as BadgeProps['variant']}>
              <FormattedMessage
                id={messages[currentStatus?.name as string].id}
                defaultMessage={messages[currentStatus?.name as string].defaultMessage}
              />
            </Badge>
          </DescriptionList.Content>
        </>
        )}
        <>
          <DescriptionList.Title>
            <FormattedMessage
              id="quote.details.submitted"
              defaultMessage="Creation Date"
            />
          </DescriptionList.Title>
          <DescriptionList.Content>
            <FormattedDate value={createdAt} />
          </DescriptionList.Content>
        </>
        <>
          <DescriptionList.Title>
            <FormattedMessage
              id="quote.details.expirey"
              defaultMessage="Expiry Date"
            />
          </DescriptionList.Title>
          <DescriptionList.Content>
            <FormattedDate value={quoteExpiryDate} />
          </DescriptionList.Content>
        </>
        <>
          <DescriptionList.Title>
            <FormattedMessage
              id="quote.details.reference"
              defaultMessage="Reference"
            />
          </DescriptionList.Title>
          <DescriptionList.Content>
            {quoteReference
              ? <span>{quoteReference}</span>
              : (
                <span className="pd-text-dim-500">
                  <FormattedMessage
                    id="quote.details.defaultQuoteReference"
                    defaultMessage="No reference"
                  />
                </span>
              )}
          </DescriptionList.Content>
        </>
        <>
          <DescriptionList.Title>
            <FormattedMessage
              id="quote.details.externalNote"
              defaultMessage="External notes"
            />
          </DescriptionList.Title>
          <DescriptionList.Content>
            {externalNote
              ? <span>{externalNote}</span>
              : (
                <span className="pd-text-dim-500">
                  <FormattedMessage
                    id="quote.details.defaultExternalNote"
                    defaultMessage="No note"
                  />
                </span>
              )}
          </DescriptionList.Content>
        </>
      </DescriptionList>

      <QuoteLineItemList
        lineItems={quoteLineItems}
        setSelectedLineItems={setSelectedLineItems}
        selectedLineItems={selectedLineItems}
      />

      <div className="pd-mb-7 pd-text-right">
        {isQuoteExpired() && (
        <Button
          onClick={openExtendQuoteModal}
          disabled={selectedLineItems.length === 0}
          className="pd-mr-5"
        >
          <FormattedMessage
            id="quotes.details.btn.extendQuote"
            defaultMessage="Extend Quote"
          />
        </Button>
        )}
        <Button
          onClick={addQuoteToCart}
          disabled={shouldDisableAddToCartButton}
          data-cy-add-quote-button="add-quote-items-to-cart"
          isLoading={addQuoteToCartMutation.isPending}
        >
          <FormattedMessage
            id="quotes.details.btn.addToCart"
            defaultMessage="Add items to cart"
          />
        </Button>
      </div>
    </>
  );
};

export default QuoteDetail;
